import semver from 'semver';
import React, { PropsWithChildren, useEffect } from 'react';

import {
  GetGameDocumentContentAsync,
  GetGameDocumentLatestAsync
} from '../services/json-document';
import { appStore } from '../stores/app-store';
import { Spinner } from '../components/spinner';
import { GameDocument } from '../types/game-document';
import {
  deleteGameFromDB,
  getGameFromDB,
  saveGameToDB
} from '../utils/game-document/indexedDB';
import { SyncAutoRouteTitles } from '../utils/game-document';
import { GameDocumentVersionResponse } from '../types/responses/game-document-version-response';
import GameNotFoundDialog from '../components/dialogs/game-not-found-dialog';
import GameAccessDeniedDialog from '../components/dialogs/game-access-denied-dialog';

const GameDocumentContext = React.createContext<
  [
    initialState: GameDocumentState,
    setState: React.Dispatch<React.SetStateAction<GameDocumentState>>
  ]
>([{ isDirty: false, isValid: true, shouldRecoverDraft: false }, () => {}]);

interface GameDocumentProviderProps extends PropsWithChildren {
  gameId: string;
}

const GameDocumentProvider: React.FC<GameDocumentProviderProps> = ({
  gameId,
  children
}) => {
  const [state, setState] = React.useState<GameDocumentState>({
    gameId: parseInt(gameId),
    isDirty: false,
    isValid: true,
    isNotFound: false,
    isAccessDenied: false
  });

  const loadDocumentContent = async () => {
    const parsedGameId = state.gameId || parseInt(gameId);
    try {
      appStore.showLoading();
      let documentLatestResponse = await GetGameDocumentLatestAsync(
        parsedGameId
      );
      if (documentLatestResponse) {
        const latestContent = await GetGameDocumentContentAsync(
          parsedGameId,
          documentLatestResponse.id!
        );

        if (latestContent) {
          const syncedContent = SyncAutoRouteTitles(latestContent);
          const content = {
            ...state,
            isLoaded: true,
            isNotFound: false,
            isAccessDenied: false,
            modifiedDateUtc: documentLatestResponse.modifiedDateUtc as any,
            gameDocument: syncedContent
          };
          setState(content);
          saveGameToDB(content);
        }
      } else {
        await deleteGameFromDB(parsedGameId);
        setState((prev) => ({
          ...prev,
          isNotFound: true,
          isAccessDenied: false,
          gameDocument: undefined
        }));
      }
    } catch (error: any) {
      console.error(error);
      if (error?.status === 403) {
        setState((prev) => ({
          ...prev,
          isAccessDenied: true,
          isNotFound: false,
          gameDocument: undefined
        }));
      } else {
        await deleteGameFromDB(parsedGameId);
        setState((prev) => ({
          ...prev,
          isNotFound: true,
          isAccessDenied: false,
          gameDocument: undefined
        }));
      }
    } finally {
      appStore.hideLoading();
    }
  };

  const loadGameDocument = async () => {
    const parsedGameId = parseInt(gameId);
    try {
      appStore.showLoading();
      const localDraft = await getGameFromDB(parsedGameId);

      let serverMetadata: GameDocumentVersionResponse | null = null;
      try {
        serverMetadata = await GetGameDocumentLatestAsync(parsedGameId);
      } catch (err: any) {
        if (err?.status === 403) {
          setState((prev) => ({
            ...prev,
            gameId: parsedGameId,
            isDirty: false,
            isValid: false,
            isAccessDenied: true,
            isNotFound: false,
            gameDocument: undefined
          }));
          return;
        }
        serverMetadata = null;
      }

      if (!serverMetadata) {
        // Game has been deleted or cannot be found on the server; purge stale local draft
        await deleteGameFromDB(parsedGameId);
        setState((prev) => ({
          ...prev,
          gameId: parsedGameId,
          isDirty: false,
          isValid: false,
          isNotFound: true,
          isAccessDenied: false,
          gameDocument: undefined
        }));
        return;
      }

      // If no local draft, just load from server
      if (!localDraft) {
        await loadDocumentContent();
        return;
      }

      if (localDraft.gameDocument) {
        localDraft.gameDocument = SyncAutoRouteTitles(localDraft.gameDocument);
      }

      const serverVersion = serverMetadata.version || '0.0.0';
      const localVersion = localDraft.gameDocument?.version || '0.0.0';

      const serverDate = serverMetadata.modifiedDateUtc
        ? new Date(serverMetadata.modifiedDateUtc).getTime()
        : 0;
      const localDate = localDraft.modifiedDateUtc
        ? new Date(localDraft.modifiedDateUtc).getTime()
        : 0;

      const isLocalNewer =
        localDate > serverDate || semver.gt(localVersion, serverVersion);
      const isServerNewer =
        serverDate > localDate || semver.gt(serverVersion, localVersion);

      if (isServerNewer && localDate !== serverDate) {
        // Older local draft exists -> server version is used, local discarded safely
        await loadDocumentContent();
      } else if (isLocalNewer && localDate !== serverDate) {
        // Newer local draft exists -> user is informed, option to recover
        setState({
          ...localDraft,
          shouldRecoverDraft: true,
          pendingRecovery: localDraft
        });
      } else {
        // Synchronized or equal version
        setState(localDraft);
      }
    } catch (err: any) {
      console.error(err);
      if (err?.status === 403) {
        setState((prev) => ({
          ...prev,
          isAccessDenied: true,
          isNotFound: false,
          gameDocument: undefined
        }));
      } else {
        await deleteGameFromDB(parsedGameId);
        setState((prev) => ({
          ...prev,
          isNotFound: true,
          isAccessDenied: false,
          gameDocument: undefined
        }));
      }
    } finally {
      appStore.hideLoading();
    }
  };

  useEffect(() => {
    loadGameDocument();
  }, [gameId]);

  if (state.isAccessDenied) {
    return (
      <GameDocumentContext.Provider value={[state, setState]}>
        <GameAccessDeniedDialog gameId={parseInt(gameId)} />
      </GameDocumentContext.Provider>
    );
  }

  if (state.isNotFound) {
    return (
      <GameDocumentContext.Provider value={[state, setState]}>
        <GameNotFoundDialog gameId={parseInt(gameId)} />
      </GameDocumentContext.Provider>
    );
  }

  return (
    <GameDocumentContext.Provider value={[state, setState]}>
      {state.gameDocument ? children : <Spinner />}
    </GameDocumentContext.Provider>
  );
};

export interface GameDocumentState {
  gameId?: number;
  gameDocument?: GameDocument;
  isDirty: boolean;
  isValid: boolean;
  isLoaded?: boolean;
  isNotFound?: boolean;
  isAccessDenied?: boolean;
  modifiedDateUtc?: string;
  shouldRecoverDraft?: boolean;
  pendingRecovery?: GameDocumentState;
}

export { GameDocumentContext, GameDocumentProvider };
